import { EarOff, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TranscriptionPauseReason } from '@hub/contracts'
import { Button } from '@/components/ui/button'

/**
 * The paused-transcription banner (board m5gc #2). Deliberately loud and a
 * little annoying: it sits across the top of the meeting for **everyone**,
 * because "HUB is not listening" is something every participant should know
 * — the notes will have a gap — and the host should feel nudged to resume.
 */
export function TranscriptionBanner({
  pausedBy,
  canResume,
  onResume,
}: {
  pausedBy: TranscriptionPauseReason
  canResume: boolean
  onResume: () => void
}) {
  const { t } = useTranslation('meet')
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="transcription-banner"
      className="flex shrink-0 items-center justify-center gap-3 bg-amber-400 px-4 py-2 text-sm font-semibold text-black shadow-[inset_0_-1px_0_rgba(0,0,0,0.15)] dark:bg-amber-500"
    >
      <EarOff className="h-4 w-4 shrink-0 animate-pulse" aria-hidden />
      <span className="min-w-0 truncate">
        {pausedBy === 'host' ? t('banner.pausedByHost') : t('banner.pausedByCredits')}
      </span>
      {canResume && pausedBy === 'host' && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 shrink-0 border-black/30 bg-white/70 px-2.5 text-xs text-black hover:bg-white"
          onClick={onResume}
        >
          <Play className="h-3 w-3" />
          {t('banner.resume')}
        </Button>
      )}
    </div>
  )
}
